import { useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MessageSquare } from 'lucide-react';
import { AssigneeAvatar } from '@/components/tasks/AssigneeAvatar';
import { cn } from '@/lib/utils';
import { AssigneePicker } from '@/components/tasks/pickers/AssigneePicker';
import { PriorityPicker } from '@/components/tasks/pickers/PriorityPicker';
import { DueDatePicker } from '@/components/tasks/pickers/DueDatePicker';

interface KanbanCardProps {
  task: {
    id: string;
    title: string;
    priority?: string;
    dueDate?: string;
    status?: string;
    statusConfig?: { isClosed?: boolean };
    assignee?: { id: string; name: string; avatarUrl?: string };
    assigneeId?: string;
    assignees?: Array<{ id: string; name: string; avatarUrl?: string }>;
    _count?: { comments?: number };
  };
  projectMembers?: Array<{ user: { id: string; name: string; avatarUrl?: string } }>;
  onClick?: () => void;
  onAssigneeChange?: (assigneeId: string | null) => void;
  onAssigneesChange?: (assigneeIds: string[]) => void;
  onDueDateChange?: (dueDate: string | null) => void;
  onPriorityChange?: (priority: string) => void;
  isDragging?: boolean;
  /** Bulk-selection mode: shows a selection checkbox on the card corner. */
  selectionMode?: boolean;
  selected?: boolean;
  onSelectChange?: (checked: boolean, shiftKey: boolean) => void;
  /** Hidden while a group drag is in flight (this card travels with the group). */
  dragHidden?: boolean;
}

export function KanbanCard({
  task,
  projectMembers,
  onClick,
  onAssigneeChange,
  onAssigneesChange,
  onDueDateChange,
  onPriorityChange,
  isDragging,
  selectionMode = false,
  selected = false,
  onSelectChange,
  dragHidden = false,
}: KanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isClosed = task.statusConfig?.isClosed ?? task.status === 'COMPLETED';
  const isOverdue =
    !!task.dueDate && new Date(task.dueDate) < new Date() && !task.dueDate.startsWith('2099');
  const commentCount = task._count?.comments || 0;
  const canPickAssignee = (!!onAssigneeChange || !!onAssigneesChange) && !!projectMembers;

  // Click-vs-drag detection (see comment in handlePointerDown).
  const clickIntentRef = useRef<{ x: number; y: number; t: number } | null>(null);

  // A small pointer move counts as a "click" (select or open); a larger move
  // (past the sensor's 8px threshold) starts a drag. This lets selection mode
  // support BOTH click-to-select and drag-to-move the selection.
  useEffect(() => {
    const handlePointerUp = (e: PointerEvent) => {
      const start = clickIntentRef.current;
      clickIntentRef.current = null;
      if (!start) return;
      const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
      const duration = Date.now() - start.t;
      if (moved < 8 && duration < 500) {
        if (selectionMode) onSelectChange?.(!selected, e.shiftKey);
        else onClick?.();
      }
    };
    document.addEventListener('pointerup', handlePointerUp, true);
    return () => document.removeEventListener('pointerup', handlePointerUp, true);
  }, [selectionMode, selected, onSelectChange, onClick]);

  const recordClickIntent = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    // Outside selection mode, ignore presses on the inline controls (they have
    // their own handlers). In selection mode those controls are inert, so any
    // press on the card is a potential click/drag.
    if (!selectionMode && target.closest('button, input, a')) return;
    clickIntentRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  };

  // Draggable in both modes. In selection mode a click selects and a drag moves
  // the whole selection (see KanbanBoard.handleDragEnd).
  const dragProps = {
    ...attributes,
    ...listeners,
    onPointerDown: (e: React.PointerEvent) => {
      listeners?.onPointerDown?.(e);
      recordClickIntent(e);
    },
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragProps}
      className={cn(
        'relative rounded-lg border p-3 transition-all cursor-grab active:cursor-grabbing',
        // While selecting, inner controls are inert so a press on them becomes a
        // card click/drag (select or move the selection) rather than an edit.
        selectionMode && 'select-none [&_button]:pointer-events-none [&_input]:pointer-events-none [&_[role=button]]:pointer-events-none',
        dragHidden && 'hidden',
        isClosed ? 'bg-grey-50 border-grey-100' : 'bg-white',
        isDragging && 'shadow-lg ring-2 ring-accent',
        selected && !isDragging && 'ring-2 ring-accent bg-accent/5',
        isSortableDragging && !isDragging && 'opacity-0',
        !isSortableDragging && !isDragging && 'shadow hover:shadow-sm',
        'group',
      )}
    >
      <p
        className={cn(
          'text-sm font-medium line-clamp-2 mb-2 transition-colors duration-150',
          !selectionMode && 'cursor-pointer',
          isClosed ? 'text-grey-400 line-through' : 'text-secondary group-hover:text-accent',
        )}
      >
        {task.title}
      </p>

      <div className="flex items-center justify-between gap-2 text-xs">
        {canPickAssignee ? (
          onAssigneesChange ? (
            <AssigneePicker
              assignees={task.assignees ?? (task.assignee ? [task.assignee] : [])}
              projectMembers={projectMembers!}
              onChangeMultiple={onAssigneesChange}
            />
          ) : (
            <AssigneePicker
              assignee={task.assignee}
              assigneeId={task.assigneeId}
              projectMembers={projectMembers!}
              onChange={onAssigneeChange!}
            />
          )
        ) : (task.assignees?.length || task.assignee) ? (
          <div className="flex items-center -space-x-1.5 flex-shrink-0">
            {(task.assignees ?? (task.assignee ? [task.assignee] : [])).slice(0, 3).map((a) => (
              <AssigneeAvatar key={a.id} person={a} className="ring-1 ring-white" />
            ))}
          </div>
        ) : null}

        <PriorityPicker priority={task.priority} onChange={onPriorityChange} />

        <DueDatePicker
          dueDate={task.dueDate}
          onChange={onDueDateChange}
          isOverdue={isOverdue}
        />

        {commentCount > 0 && (
          <div className="flex items-center gap-1 text-grey-500 flex-shrink-0">
            <MessageSquare className="w-3 h-3" />
            <span>{commentCount}</span>
          </div>
        )}

        <div className="flex-1" />
      </div>
    </div>
  );
}
